import React, { useState, forwardRef, useMemo, useCallback, useRef } from 'react';
import _, { forEach, isEmpty } from 'lodash';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import GenericMappingForm from '../GenericMappingForm';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import { Form } from "antd";

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  type: 'tags',
  nexposeProjectList: [{ tags: undefined }]
};

const NexposeMappingForm = forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [{ ...initialFormValues }];
  });

  const [mappedProjectList] = useState(() => {
    if (props.record) {
      const { siteId, siteName, tagId, tagName } = props.record;
      const tagIdArray = _.isEmpty(tagId) ? [] : tagId.split(',');
      const tagNameArray = _.isEmpty(tagName) ? [] : tagName.split(',')
      return {
        sites: siteId ? [{ id: siteId, name: siteName }] : [],
        tags: tagId ? tagIdArray.map((id, index) => ({ id, name: tagNameArray[index] })) : []
      };
    }
    return { sites: [], tags: [] };
  });

  const siteApiRef = useRef({ type: "sites" });
  const tagApiRef = useRef({ type: "tags" });
  const siteRef = useRef(_.mapValues(_.keyBy(mappedProjectList.sites, 'id'), 'name'));
  const tagRef = useRef(_.mapValues(_.keyBy(mappedProjectList.tags, 'id'), 'name'));

  const fetchSitesOrTags = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });

    return {
      promise: promise.then(response => {
        if (params.type === "sites") {
          if (mappedProjectList.sites && !isEmpty(response.projects)) {
            response.projects = [...response.projects, ...mappedProjectList.sites];
          }
          forEach(response.projects, p => {
            siteRef.current[p.id] = p.name;
          });
        }

        if (params.type === "tags") {
          if (mappedProjectList.tags && !isEmpty(response.projects)) {
            response.projects = [...response.projects, ...mappedProjectList.tags];
          }
          forEach(response.projects, p => {
            tagRef.current[p.id] = p.name;
          });
        }
        return response;
      }),
      cancel
    };
  }, [props.toolName, props.loginConfigId, mappedProjectList]);

  const projectListRender = useCallback((listField) => {
    const siteFields = [
      {
        name: [listField.name, "siteId"],
        label: 'Rapid7 Nexpose Sites',
        required: true,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        props: {
          api: fetchSitesOrTags,
          apiParams: siteApiRef.current,
          allowClear: true
        }
      }
    ];

    const tagFields = [
      {
        name: [listField.name, "tagId"],
        label: 'Rapid7 Nexpose Tags',
        required: true,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        props: {
          api: fetchSitesOrTags,
          apiParams: tagApiRef.current,
          allowClear: true,
          mode: "multiple"
        }
      }
    ];

    const selectedType = form.getFieldsValue().configurations[listField.name]?.type;

    return (
      <div className='m-b-sm'>
        {
          selectedType === 'sites'
            ? generateFormFields(siteFields)
            : generateFormFields(tagFields)
        }
      </div>
    );
  }, [fetchSitesOrTags, form]);

  const extraFields = useMemo(() => ([
    {
      name: 'type',
      type: FieldTypes.SELECT,
      label: 'Select Type',
      colSpan: 10,
      options: [
        { label: 'Sites', value: 'sites' },
        { label: 'Tags', value: 'tags' }
      ],
      required: true,
    },
    {
      name: (listField) => [listField.name, "nexposeProjectList"],
      render: projectListRender,
      colSpan: 12
    },
  ]), [projectListRender]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { tagId } = config
    return {
      tagId: _.isEmpty(tagId) ? [] : tagId.split(',')
    }
  }, []);

  const getExtraFieldsData = useCallback((config) => {
    const { type, tagId, siteId } = config;
    let tagName;
    if (type === 'tags') {
      tagName = tagId.map(id => tagRef.current[id])
    }
    return {
      ...(type === 'sites' ? { siteId, siteName: siteRef.current[siteId] } : {}),
      ...(type === 'tags' ? { tagId: tagId.join(','), tagName: tagName.join(',') } : {}),
      type,
    };
  }, []);

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      title="Map Rapid7 Nexpose Sites or Tags"
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      {...props}
    />
  );
});

export default NexposeMappingForm;
